import { User } from "@/types/v2";

/**
 * Extracts and returns the initials from a user's display name.
 * @param {string} displayName - The display name of the user. Can be null or undefined.
 * @returns {string} The initials extracted from the display name. Returns an empty string if the display name is null or undefined.
 */
export function getUsersInitials(displayName: string | null | undefined) {
  if (!displayName) return "";

  // Split the display name into an array of words
  const words = displayName.trim().split(/\s+/);

  // Extract the first letter from each of the first two words and concatenate them
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
}

/**
 * Extracts the first name from a given display name.
 * @param {string | null | undefined} displayName - The display name from which to extract the first name.
 * @returns {string} The first name extracted from the display name or an empty string if the display name is null, undefined, or empty.
 */
export function getUsersFirstName(displayName: string | null | undefined) {
  if (!displayName) return "";
  const words = displayName.trim().split(/\s+/);
  return words[0];
}

/**
 * Extracts display name from a user object with fallback to first/last name
 * @param {User | null} user - The user object
 * @returns {string} The user's display name
 */
export function extractUserDisplayName(user: User | null): string {
  if (!user) {
    return "Unknown User";
  }

  // Try display name first
  if (user.displayName && user.displayName.trim() !== "") {
    return user.displayName;
  }

  // Fallback to first and/or last name
  const firstName = user.firstName || "";
  const lastName = user.lastName || "";

  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  } else if (firstName) {
    return firstName;
  } else if (lastName) {
    return lastName;
  }

  // Final fallback to user ID
  return "Unknown User";
}
